import React from 'react';
import PropTypes from 'prop-types';
import TableWindow from '../../components/Table/TableWindow';
import useTablePages from '../../hooks/useTablePages';
import availableColumns from './Customers.columns';
import { useOrderBy } from '../../hooks/useOrderBy';
import PageBar from '../../components/PageBar/PageBar';
import { PortalWithState } from 'react-portal';
import Button from '../../components/Button/Button';
import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../hooks/useThirdPaneCallbacks';
import CustomerThirdPane from '../../components/Customer/CustomerThirdPane';
import CustomerEditModal from '../../components/Customer/CustomerEditModal';

import ThirdPaneLayout, {
  MainColumn,
} from '../../components/Layout/ThirdPaneLayout';
import FilterBarCustomer, {
  CustomerFilters,
  defaultFilters,
} from './FilterBarCustomer';
import useMode from '../../hooks/useMode';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import useStoredFilters from '../../hooks/useStoredFilters';
import { useHistory } from 'react-router-dom';
import { GET_CUSTOMER_IDS, GET_CUSTOMERS } from './Customers.queries';
import { getStringSearchOr } from '../../helpers/search';

export type CustomerItem = {
  id: string;
  accountNumber: string;
  status: string;
  profile: {
    name: string;
    phones: {
      value: string;
    }[];
    emails: {
      value: string;
    }[];
    contactAddress?: {
      street1: string;
      street2: string;
      city: string;
      state: string;
      zip: string;
      zipExt: string;
    };
  };
};

type CustomerDetailParams = {
  id?: string;
};

type CustomerDetailProps = RouteComponentProps<CustomerDetailParams>;

export const searchFields = [
  {
    field: 'profile.name',
    label: 'Name',
  },
  {
    field: 'profile.firstName',
    label: null,
  },
  {
    field: 'profile.lastName',
    label: null,
  },
  {
    field: 'accountNumber',
    label: 'Account',
  },
  {
    field: 'profile.contactAddress.street1',
    label: 'Address',
  },
  {
    field: 'profile.contactAddress.street2',
    label: null,
  },
  {
    field: 'profile.phones.value',
    label: 'Phone',
  },
  {
    field: 'profile.emails.value',
    label: 'Email',
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

const convertFilters = (storedFilters: CustomerFilters) => {
  return {
    status: storedFilters.status,
    ...convertStringSearch(storedFilters.stringSearch),
  };
};

/**
 * @component Used for customers page
 */
function Customers({ match }: CustomerDetailProps): JSX.Element {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<CustomerFilters>(
      convertFilters,
      'customers',
      defaultFilters
    );

  const mode = useMode();
  const history = useHistory();

  const { orderBy, onHeaderClick } = useOrderBy(
    'customers',
    availableColumns,
    'profile.name'
  );

  const { loading, error, itemIds, selectedIds, setSelectedIds, itemLoader } =
    useTablePages<CustomerItem>(
      'customers',
      GET_CUSTOMER_IDS,
      {
        where: queryFilters,
        limit: null,
        orderBy,
      },
      GET_CUSTOMERS,
      (ids) => ({
        where: {
          id: ids,
        },
        limit: null,
        orderBy,
      })
    );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'customers');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${useMode()}/customers`, itemIds, match.params.id);

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Customers">
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  ml={3}
                  variant="primary"
                  icon="plus"
                  onClick={openPortal}
                >
                  Add Customer
                </Button>
                {portal(
                  <CustomerEditModal
                    action="add"
                    onSave={(customer) => {
                      history.push(`/${mode}/customers/${customer.id}`);
                    }}
                    onClose={closePortal}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </PageBar>
        <FilterBarCustomer
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <CustomerThirdPane
        customerId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

Customers.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Customers);
